import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Modal } from 'react-bootstrap'
import './AddChatStyle.css'
import '../ModalStyle.css'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { IContactGroup } from '@dataTypes/contact.type'
import StatusBadge from '@components/common/StatusBadge/StatusBadge.component'
import {
  ChatChannel,
  IChatMessage,
  IChatParticipant,
} from '@dataTypes/chat.type'
import {
  createConversation,
  updateConversation,
  updateSelectedMessage,
} from '@store/actions'
import { getMetaChannel } from '@utils/chat.util'
import Avatar from '@components/common/Avatar/Avatar.component'
import {
  type AddChatRecipient,
  buildRecentlyContactedRecipients,
  buildRecipientFromContact,
} from './addChatModal.util'

type AddChatModalProps = {
  setShow: Dispatch<SetStateAction<boolean>>
  show: boolean
}

const contactBtns = [
  {
    channel: 'SMS' as ChatChannel,
    icon: 'ti ti-message-filled',
    className: 'sms-btn',
    tooltip: 'SMS',
    key: 'sms' as const,
  },
  {
    channel: 'Messenger' as ChatChannel,
    icon: 'mgc_messenger_fill',
    className: 'messenger-btn',
    tooltip: 'Messenger',
    key: 'messenger' as const,
  },
  {
    channel: 'Instagram' as ChatChannel,
    icon: 'mgc_ins_fill',
    className: 'instagram-btn',
    tooltip: 'Instagram',
    key: 'instagram' as const,
  },
]

function buildGroupRecipient(group: IContactGroup): AddChatRecipient {
  return {
    name: group.group_name,
    type: 'group',
    id: group.contact_group_id!,
    public_id: '',
    phoneDisplay: '—',
    sms: group.channel === 'SMS',
    messenger: group.channel === 'Messenger',
    instagram: group.channel === 'Instagram',
    group,
  }
}

const AddChatModal = (props: AddChatModalProps): React.ReactElement => {
  const dispatch = useAppDispatch()
  const contacts = useAppSelector((state) => state.contacts.data)
  const contactGroups = useAppSelector((state) => state.contactGroup.data)
  const conversations = useAppSelector((state) => state.chat.data)
  const [name, setName] = useState<string>('')
  const [isSearchFocused, setIsSearchFocused] = useState(false)

  const allRecipients = useMemo(() => {
    const recipients: AddChatRecipient[] =
      contactGroups.map(buildGroupRecipient)
    contacts.forEach((contact) => {
      recipients.push(buildRecipientFromContact(contact))
    })
    return recipients
  }, [contacts, contactGroups])

  const recentlyContacted = useMemo(() => {
    return buildRecentlyContactedRecipients(conversations, contacts)
  }, [conversations, contacts])

  const recipientList = useMemo(() => {
    const query = name.trim().toLowerCase()
    if (!query) return []
    return allRecipients.filter((recipient) => {
      const nameMatch = recipient.name.toLowerCase().includes(query)
      const phoneMatch = recipient.phoneDisplay.toLowerCase().includes(query)
      const statusMatch = recipient.status?.toLowerCase().includes(query)
      return nameMatch || phoneMatch || statusMatch
    })
  }, [allRecipients, name])

  const isSearching = name.trim().length > 0
  const modalTitle = isSearching ? 'Start chat' : 'New chat'
  const listHeader = isSearching ? 'Results' : 'Recently contacted'
  const visibleRecipients = isSearching ? recipientList : recentlyContacted

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setName(e.target.value)
  }

  const highlightText = (text: string, highlight: string): React.ReactNode => {
    if (!text.length || !highlight.trim().length) {
      return text
    }
    const regExp = new RegExp(`(${highlight.trim()})`, 'gi')
    const parts = text.split(regExp)
    return parts.map((part, index) =>
      part.toLowerCase() === highlight.trim().toLowerCase() ? (
        <span key={index} className="search-highlight">
          {part}
        </span>
      ) : (
        part
      ),
    )
  }

  const onButtonClick = async (
    channel: ChatChannel,
    recipient: AddChatRecipient,
  ): Promise<void> => {
    if (!recipient.contact && !recipient.group) return

    const index = conversations?.findIndex((convo) => {
      if (recipient.type === 'contact') {
        return (
          convo.chat_participant.length === 1 &&
          convo.chat_participant[0].contact_id === recipient.contact?.contact_id
        )
      }
      if (recipient.type === 'group') {
        const groupContactIds = recipient.group?.contacts
          ?.map((c) => c.contact_id)
          .filter((id) => id !== undefined)
        if (!groupContactIds || groupContactIds.length === 0) return false
        if (convo.chat_participant.length !== groupContactIds.length) {
          return false
        }
        const convoContactIds = convo.chat_participant
          .map((p) => p.contact_id)
          .filter((id) => id !== undefined)
        return (
          convoContactIds.length === groupContactIds.length &&
          convoContactIds.every((id) => groupContactIds.includes(id)) &&
          groupContactIds.every((id) => convoContactIds.includes(id))
        )
      }
      return false
    })

    if (index === undefined || index === -1) {
      let members_data: IChatParticipant[] = []
      if (recipient.type === 'contact' && recipient.contact) {
        members_data = [
          {
            chat_conversation_id: '',
            meta_channel: getMetaChannel(channel),
            contact_id: recipient.contact.contact_id!,
            phone_number: recipient.contact.cellphone!,
            username: recipient.contact.patient?.meta_username,
            photo: recipient.contact.patient?.photo,
          },
        ]
      } else if (recipient.type === 'group' && recipient.group) {
        members_data =
          recipient.group.contacts?.map((c) => ({
            chat_conversation_id: '',
            meta_channel: getMetaChannel(channel),
            contact_id: c.contact_id!,
            username: c.patient?.meta_username,
            photo: c.patient?.photo,
          })) || []
      }

      const conversation_data = {
        name: recipient.type === 'group' ? recipient.name : undefined,
        channel,
        chat_message: [] as IChatMessage[],
        chat_participant: [] as IChatParticipant[],
      }
      dispatch(createConversation({ conversation_data, members_data }))
    } else {
      if (!conversations) return
      const isArchived = conversations[index].is_archived
      if (isArchived) {
        await dispatch(
          updateConversation({
            id: conversations[index].chat_conversation_id as string,
            data: { is_archived: false, date_time_archived_at: null },
          }),
        )
      }
      dispatch(
        updateSelectedMessage({
          id: conversations[index].chat_conversation_id as string,
        }),
      )
    }

    props.setShow(false)
  }

  const onHide = useCallback((): void => {
    props.setShow(false)
    setName('')
    setIsSearchFocused(false)
  }, [props])

  useEffect(() => {
    if (!props.show) onHide()
  }, [props.show, onHide])

  const renderAvatar = (recipient: AddChatRecipient): React.ReactNode => {
    if (recipient.isUnknown) {
      return (
        <div className="img-container unknown">
          <span className="mgc_user_2_fill" />
        </div>
      )
    }

    if (recipient.photo) {
      return (
        <Avatar
          source={recipient.photo}
          name={recipient.name}
          className="patient-img"
        />
      )
    }

    return (
      <div className={`img-container ${recipient.type}`}>
        <span
          className={`mgc_${recipient.type === 'contact' ? 'user_2' : 'group_3'}_fill`}
        />
      </div>
    )
  }

  const renderRecipientRow = (
    recipient: AddChatRecipient,
    index: number,
  ): React.ReactNode => {
    const showStatusBadge = isSearching && recipient.status !== undefined
    const subline = recipient.isUnknown
      ? (recipient.secondaryLabel ?? 'Unknown')
      : recipient.phoneDisplay

    return (
      <div key={`${recipient.id}-${index}`} className="contact-dropdown-item">
        {renderAvatar(recipient)}

        <div className="info-container">
          <div className="ic-text">
            <span className="full-name">
              {isSearching
                ? highlightText(recipient.name, name)
                : recipient.name}
            </span>
            <div className="secondary">
              <span
                className={`phone-line${
                  recipient.isUnknown ? ' phone-line--unknown' : ''
                }`}
              >
                {subline}
              </span>
              {showStatusBadge ? (
                <StatusBadge status={recipient.status!} />
              ) : null}
            </div>
          </div>

          <div className="contact-btns">
            {contactBtns.map((btn) =>
              recipient[btn.key] ? (
                <button
                  key={btn.key}
                  type="button"
                  title={btn.tooltip}
                  className={btn.className}
                  onClick={() => onButtonClick(btn.channel, recipient)}
                  disabled={btn.channel !== 'SMS'}
                >
                  <span className={btn.icon} />
                </button>
              ) : null,
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <Modal
      show={props.show}
      onHide={onHide}
      centered
      className="add-chat-modal"
    >
      <Modal.Header closeButton className="add-chat-modal-header">
        <Modal.Title>{modalTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="add-chat-search-section">
          <div
            className={`add-chat-search${
              isSearchFocused || isSearching ? ' add-chat-search--active' : ''
            }`}
          >
            <span className="add-chat-search-icon" aria-hidden="true">
              <i className="ti ti-search" />
            </span>
            <input
              type="text"
              className="add-chat-search-field"
              placeholder="Search patient name"
              value={name}
              onChange={handleNameChange}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
            />
          </div>
        </div>

        {visibleRecipients.length > 0 ? (
          <div className="acm-list-section">
            <span className="acm-header">{listHeader}</span>
            <div className="contact-dropdown-items">
              {visibleRecipients.map((recipient, index) =>
                renderRecipientRow(recipient, index),
              )}
            </div>
          </div>
        ) : isSearching ? (
          <div className="acm-list-section">
            <span className="acm-header">{listHeader}</span>
            <div className="acm-empty-results">No patients found</div>
          </div>
        ) : null}
      </Modal.Body>
    </Modal>
  )
}

export default AddChatModal
